import { useEffect, useState } from "react";
import { GetStatisticsUniversityGenderConfig } from "src/service/api";
import { StatisticsUniversityGenderTypes } from "src/static/interface";

export interface UniversityData {
  name: string;
  percentage: number;
  total: number;
  students: number;
  completed: number;
}

export interface GenderDistributionData {
  name: string;
  value: number;
  color: string;
  count: number;
}

// Custom hook for dashboard statistics
export const useDashboardStatistics = (selectedQuizRuleId: number | null) => {
  const [loadingCharts, setLoadingCharts] = useState(false);
  const [genderDistributionData, setGenderDistributionData] = useState<
    GenderDistributionData[]
  >([]);
  const [top10ByPercentage, setTop10ByPercentage] = useState<UniversityData[]>(
    []
  );
  const [bottom10ByPercentage, setBottom10ByPercentage] = useState<
    UniversityData[]
  >([]);
  const [top10ByCompleted, setTop10ByCompleted] = useState<UniversityData[]>(
    []
  );
  const [bottom10ByCompleted, setBottom10ByCompleted] = useState<
    UniversityData[]
  >([]);
  const [totalUsers, setTotalUsers] = useState<number>(0);
  const [totalStudents, setTotalStudents] = useState<number>(0);
  const [totalNotCompleted, setTotalNotCompleted] = useState<number>(0);
  const [maleUsers, setMaleUsers] = useState<number>(0);
  const [femaleUsers, setFemaleUsers] = useState<number>(0);

  useEffect(() => {
    if (!selectedQuizRuleId) return;

    setLoadingCharts(true);

    // Barcha ma'lumotlarni GetStatisticsUniversityGenderConfig API dan olish
    GetStatisticsUniversityGenderConfig({
      quizRuleId: selectedQuizRuleId,
    })
      .then((res) => {
        const data: StatisticsUniversityGenderTypes[] = res.data || [];

        // Faqat valid participationRate ga ega bo'lgan ma'lumotlarni filterlab olish
        const validData = data.filter(
          (item) =>
            item.participationRate != null &&
            !isNaN(item.participationRate) &&
            item.participationRate >= 0
        );

        // Qatnashganlar va jami talabalar sonini hisoblash
        let totalCompleted = 0;
        let totalStudents = 0;
        let totalMaleCompleted = 0;
        let totalFemaleCompleted = 0;

        validData.forEach((item) => {
          const totalMale = item.internalMale + item.externalMale;
          const totalFemale = item.internalFemale + item.externalFemale;
          const total = totalMale + totalFemale;
          const completed = Math.round((total * item.participationRate) / 100);
          
          totalCompleted += completed;
          totalStudents += total;
          
          // Jins bo'yicha qatnashganlarni hisoblash
          if (total > 0) {
            const maleRatio = totalMale / total;
            const femaleRatio = totalFemale / total;
            totalMaleCompleted += Math.round(completed * maleRatio);
            totalFemaleCompleted += Math.round(completed * femaleRatio);
          }
        });

        // Statistik kartalar uchun qatnashganlar ma'lumotlari
        setTotalUsers(totalCompleted);
        setTotalStudents(totalStudents);
        setTotalNotCompleted(totalStudents - totalCompleted);
        setMaleUsers(totalMaleCompleted);
        setFemaleUsers(totalFemaleCompleted);

        // Gender Distribution Data (qatnashganlar bo'yicha)
        const malePercent = totalCompleted > 0 ? (totalMaleCompleted / totalCompleted) * 100 : 0;
        const femalePercent = totalCompleted > 0 ? (totalFemaleCompleted / totalCompleted) * 100 : 0;
        setGenderDistributionData([
          {
            name: "Erkaklar",
            value: Math.round(malePercent * 10) / 10,
            color: "#4facfe",
            count: totalMaleCompleted,
          },
          {
            name: "Ayollar",
            value: Math.round(femalePercent * 10) / 10,
            color: "#f093fb",
            count: totalFemaleCompleted,
          },
        ]);

        // Barcha ma'lumotlarni map qilish
        const allData = validData.map((item) => {
          const totalMale = item.internalMale + item.externalMale;
          const totalFemale = item.internalFemale + item.externalFemale;
          const total = totalMale + totalFemale;
          const completed = Math.round((total * item.participationRate) / 100);
          return {
            name: item.university,
            percentage: item.participationRate,
            total: total,
            students: total,
            completed: completed,
          };
        });

        // Qatnashish foizi bo'yicha Top 10 va Bottom 10
        const sortedByPercentageDesc = [...allData].sort(
          (a, b) => b.percentage - a.percentage
        );
        const top10ByPercentage = sortedByPercentageDesc.slice(0, 10);

        const sortedByPercentageAsc = [...allData].sort(
          (a, b) => a.percentage - b.percentage
        );
        const bottom10ByPercentage = sortedByPercentageAsc.slice(0, 10);

        // Qatnashganlar soni bo'yicha Top 10 va Bottom 10
        const sortedByCompletedDesc = [...allData].sort(
          (a, b) => b.completed - a.completed
        );
        const top10ByCompleted = sortedByCompletedDesc.slice(0, 10);

        const sortedByCompletedAsc = [...allData].sort(
          (a, b) => a.completed - b.completed
        );
        const bottom10ByCompleted = sortedByCompletedAsc.slice(0, 10);

        setTop10ByPercentage(top10ByPercentage);
        setBottom10ByPercentage(bottom10ByPercentage);
        setTop10ByCompleted(top10ByCompleted);
        setBottom10ByCompleted(bottom10ByCompleted);
        setLoadingCharts(false);
      })
      .catch(() => {
        setTotalUsers(0);
        setTotalStudents(0);
        setTotalNotCompleted(0);
        setMaleUsers(0);
        setFemaleUsers(0);
        setGenderDistributionData([]);
        setTop10ByPercentage([]);
        setBottom10ByPercentage([]);
        setTop10ByCompleted([]);
        setBottom10ByCompleted([]);
        setLoadingCharts(false);
      });
  }, [selectedQuizRuleId]);

  return {
    loadingCharts,
    genderDistributionData,
    top10ByPercentage,
    bottom10ByPercentage,
    top10ByCompleted,
    bottom10ByCompleted,
    totalUsers,
    totalStudents,
    totalNotCompleted,
    maleUsers,
    femaleUsers,
  };
};

